/* =========================================================================
   OrganizeMee — Mobile CRO behaviour
   Guide steps D2, D3, D6, D9, D10, D11, D12.

   Design notes
   - No scroll listeners. Visibility work uses IntersectionObserver (D3, D10).
   - Variant changes patch only the few text nodes that changed; the product
     area is never re-rendered (D10 INP).
   - The sticky bar forwards its click to the real Add to Cart button, so the
     theme's own cart handler fires exactly once and add_to_cart cannot
     double-fire (D12).
   - All configuration arrives from Liquid via the OMCro config element, so
     nothing here needs editing to change behaviour.
   ========================================================================= */

(function () {
  'use strict';

  var cfg = {};
  try {
    var el = document.getElementById('om-cro-config');
    if (el) cfg = JSON.parse(el.textContent);
  } catch (e) {
    /* keep defaults */
  }

  /* ---------------------------------------------------------------- money */

  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var fmt = format || cfg.moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function part(number, precision, thousands, decimal) {
      if (isNaN(number) || number === null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ','));
      var tail = parts[1] ? (decimal || '.') + parts[1] : '';
      return head + tail;
    }

    var match = fmt.match(placeholder);
    if (!match) return fmt;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = part(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = part(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = part(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = part(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = part(cents, 0, ' ', ',');
        break;
      default:
        value = part(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  // Mirrors Liquid's money_without_trailing_zeros so the JS-updated price
  // matches the server-rendered one exactly.
  function money(cents) {
    var out = formatMoney(cents);
    return out.replace(/([.,])00(?!\d)/, '');
  }

  function show(node, visible) {
    if (!node) return;
    node.hidden = !visible;
  }

  function text(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  /* ----------------------------------------------------- D2: price block */

  function ProductArea(root) {
    this.root = root;
    this.form = root.querySelector('form[action*="/cart/add"]') || root.querySelector('form');
    this.idInput = this.form && this.form.querySelector('input[name="id"]');
    this.variants = [];

    if (this.form) {
      var raw = this.form.getAttribute('data-variants');
      if (raw) {
        try {
          this.variants = JSON.parse(raw);
        } catch (e) {
          this.variants = [];
        }
      }
    }

    this.priceEl = root.querySelector('[data-om-price]');
    this.compareEl = root.querySelector('[data-om-compare]');
    this.discountEl = root.querySelector('[data-om-discount]');
    this.savingsEl = root.querySelector('[data-om-savings]');
    this.atcBtn = root.querySelector('[data-om-atc]');
    this.atcLabel = root.querySelector('[data-om-atc-label]');
    this.atcPrice = root.querySelector('[data-om-atc-price]');
    this.statusEl = root.querySelector('[data-om-status]');

    this.bindQuantity();
    this.bindVariants();
  }

  ProductArea.prototype.currentVariant = function () {
    if (!this.idInput || !this.variants.length) return null;
    var id = parseInt(this.idInput.value, 10);
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].id === id) return this.variants[i];
    }
    return null;
  };

  ProductArea.prototype.render = function (v) {
    if (!v) return;

    var onSale = v.compare_at_price && v.compare_at_price > v.price;
    var saving = onSale ? v.compare_at_price - v.price : 0;

    text(this.priceEl, money(v.price));

    if (this.compareEl) {
      show(this.compareEl, !!onSale);
      if (onSale) text(this.compareEl, money(v.compare_at_price));
    }

    if (this.discountEl) {
      show(this.discountEl, !!onSale);
      if (onSale) {
        text(
          this.discountEl,
          cfg.discountFormat === 'amount'
            ? money(saving) + ' OFF'
            : Math.round((saving * 100) / v.compare_at_price) + '% OFF'
        );
      }
    }

    if (this.savingsEl) {
      show(this.savingsEl, !!onSale);
      if (onSale) text(this.savingsEl, 'You save ' + money(saving));
    }

    if (this.atcBtn) {
      this.atcBtn.disabled = !v.available;
      text(this.atcLabel, v.available ? cfg.ctaLabel : cfg.soldOutLabel);
      if (this.atcPrice) text(this.atcPrice, money(v.price));
    }

    document.dispatchEvent(
      new CustomEvent('om:variant:change', { detail: { variant: v } })
    );
  };

  ProductArea.prototype.bindVariants = function () {
    if (!this.form || !this.idInput) return;
    var self = this;
    var last = this.idInput.value;

    // The theme's variant picker updates the hidden id input in its own
    // change handler. Re-read it after the event loop turn rather than
    // duplicating the theme's option-matching logic.
    var sync = function () {
      if (self.idInput.value === last) return;
      last = self.idInput.value;
      self.render(self.currentVariant());
    };

    this.form.addEventListener('change', function () {
      setTimeout(sync, 0);
      setTimeout(sync, 120);
    });

    // Apps and quick-view flows may set the value programmatically.
    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(this.idInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }
  };

  /* -------------------------------------------------- D2/D11: quantity */

  ProductArea.prototype.bindQuantity = function () {
    var wrap = this.root.querySelector('[data-om-qty]');
    if (!wrap) return;

    var input = wrap.querySelector('[data-om-qty-input]');
    var down = wrap.querySelector('[data-om-qty-down]');
    var up = wrap.querySelector('[data-om-qty-up]');
    if (!input) return;

    var self = this;
    var min = parseInt(input.min, 10) || 1;
    var step = parseInt(input.step, 10) || 1;

    function set(next) {
      var value = Math.max(min, next);
      input.value = value;
      if (down) down.disabled = value <= min;
      // Announce the change for assistive technology (guide D11).
      if (self.statusEl) self.statusEl.textContent = 'Quantity ' + value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (down) {
      down.addEventListener('click', function () {
        set((parseInt(input.value, 10) || min) - step);
      });
    }
    if (up) {
      up.addEventListener('click', function () {
        set((parseInt(input.value, 10) || min) + step);
      });
    }
    input.addEventListener('blur', function () {
      set(parseInt(input.value, 10) || min);
    });

    if (down) down.disabled = (parseInt(input.value, 10) || min) <= min;
  };

  /* ----------------------------------------------- D3: sticky Add to Cart */

  function StickyAtc(bar) {
    var target = document.querySelector('[data-om-atc]');
    if (!target) return;

    var btn = bar.querySelector('[data-om-sticky-btn]');
    var minWidth = parseInt(bar.getAttribute('data-mobile-max') || '0', 10);

    function allowed() {
      return !minWidth || window.matchMedia('(max-width: ' + minWidth + 'px)').matches;
    }

    // Show the bar only once the real CTA has actually LEFT the viewport, and
    // hide it again the moment the CTA returns (guide D3).
    //
    // "Leaves viewport" is deliberately not the same as "is off-screen": on
    // first paint the real CTA sits below the fold, and showing the sticky bar
    // then would cover the gallery before the shopper has even reached the buy
    // box. The bar therefore waits until the CTA has been seen at least once
    // and has since scrolled above the viewport.
    var seen = false;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              seen = true;
              bar.classList.remove('is-visible');
              return;
            }
            var scrolledPast = entry.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', seen && scrolledPast && allowed());
          });
        },
        { threshold: 0 }
      ).observe(target);
    }

    window.addEventListener('resize', function () {
      if (!allowed()) bar.classList.remove('is-visible');
    });

    // Forward to the real button so the theme's own add-to-cart handler runs
    // once. Tagged separately for analytics (guide D12).
    if (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();

        // Guide step D12: "Tag sticky-CTA clicks separately if analytics
        // architecture allows." This is a distinct attribution signal only —
        // the add_to_cart event itself is still fired once, by the theme's own
        // handler, because the click is forwarded to the real button below.
        var detail = { source: 'sticky_atc', productUrl: location.pathname };
        document.dispatchEvent(new CustomEvent('om:sticky_atc:click', { detail: detail }));

        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event: 'om_sticky_atc_click', cta_source: 'sticky_atc' });
        }

        target.click();
      });
    }

    // Keep the sticky bar in step with the selected variant.
    document.addEventListener('om:variant:change', function (event) {
      var v = event.detail.variant;
      if (!v) return;

      text(bar.querySelector('[data-om-sticky-price]'), money(v.price));

      var cmp = bar.querySelector('[data-om-sticky-compare]');
      if (cmp) {
        var onSale = v.compare_at_price && v.compare_at_price > v.price;
        show(cmp, !!onSale);
        if (onSale) text(cmp, money(v.compare_at_price));
      }

      if (btn) {
        btn.disabled = !v.available;
        text(btn, v.available ? cfg.stickyCta : cfg.stickySoldOut);
      }
    });

    document.body.classList.add('om-has-sticky-atc');
  }

  /* ------------------------------------------------- D6: collapsible tools */

  function bindCollapsibles(scope) {
    (scope || document)
      .querySelectorAll('[data-om-collapsible-trigger]')
      .forEach(function (trigger) {
        if (trigger.dataset.omBound) return;
        trigger.dataset.omBound = '1';

        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;

        trigger.addEventListener('click', function () {
          var open = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', String(!open));
          panel.hidden = open;
          if (!open) {
            var field = panel.querySelector('input, textarea, select');
            if (field) field.focus();
          }
        });
      });
  }

  /* ---------------------------------------------- D7: recently viewed */

  // Reveal the module only once the list actually has items, so a first-time
  // visitor never sees "Your list is currently empty".
  function bindRecentlyViewed() {
    document.querySelectorAll('.om-rv-hide-empty').forEach(function (node) {
      var list = node.querySelector('.recently-viewed-product-list-wrapper');
      if (!list) return;

      var update = function () {
        node.classList.toggle('om-rv-has-items', !list.hasAttribute('hidden'));
      };
      update();

      if ('MutationObserver' in window) {
        new MutationObserver(update).observe(list, {
          attributes: true,
          attributeFilter: ['hidden'],
        });
      }
    });
  }


  /* --------------------------------------------- Stat counters */

  // Counts each figure up from zero the first time its row scrolls into view,
  // then disconnects. The final text is already in the DOM from Liquid, so if
  // this never runs — or the visitor prefers reduced motion — the correct
  // figures are on screen anyway.
  function bindStats() {
    var rows = document.querySelectorAll('[data-om-stats]');
    if (!rows.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    rows.forEach(function (row) {
      if (row.dataset.omStatsBound) return;
      row.dataset.omStatsBound = '1';

      if (reduced || row.getAttribute('data-animate') === 'false') return;
      if (!('IntersectionObserver' in window)) return;

      var duration = parseInt(row.getAttribute('data-duration'), 10) || 1600;

      var items = Array.prototype.map
        .call(row.querySelectorAll('[data-om-stat-value]'), function (el) {
          var full = el.getAttribute('data-om-stat-value') || el.textContent;
          // Split into: anything before the digits, the number itself, the rest.
          var m = full.match(/^(\D*)([\d.,]+)([\s\S]*)$/);
          if (!m) return null;

          var raw = m[2];
          var decimals = (raw.split('.')[1] || '').length;
          var grouped = raw.indexOf(',') !== -1;
          var target = parseFloat(raw.replace(/,/g, ''));
          if (isNaN(target)) return null;

          return { el: el, prefix: m[1], suffix: m[3], target: target, decimals: decimals, grouped: grouped };
        })
        .filter(Boolean);

      if (!items.length) return;

      function format(item, value) {
        var out = value.toFixed(item.decimals);
        if (item.grouped) {
          var parts = out.split('.');
          // Indian grouping would differ; only apply it when the source used
          // plain thousands separators.
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          out = parts.join('.');
        }
        return item.prefix + out + item.suffix;
      }

      // Start from zero so the first painted frame is the start of the count.
      items.forEach(function (item) { item.el.textContent = format(item, 0); });

      var run = function () {
        var start = null;
        var step = function (ts) {
          if (start === null) start = ts;
          var p = Math.min((ts - start) / duration, 1);
          // Ease-out, so it decelerates into the final figure.
          var eased = 1 - Math.pow(1 - p, 3);
          items.forEach(function (item) {
            item.el.textContent = format(item, item.target * eased);
          });
          if (p < 1) requestAnimationFrame(step);
          else items.forEach(function (item) {
            item.el.textContent = item.el.getAttribute('data-om-stat-value');
          });
        };
        requestAnimationFrame(step);
      };

      new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.disconnect();
          run();
        });
      }, { threshold: 0.35 }).observe(row);
    });
  }

  /* ------------------------------------ Quick add (upsell rows)  */

  // Upsell rows sit inside the main product form, so they cannot carry a form
  // of their own. They post through the theme's cart element instead, which is
  // the same path the main button uses — one add, drawer opens, no navigation.
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[data-om-add]');
    if (!btn) return;

    event.preventDefault();
    var id = parseInt(btn.getAttribute('data-om-add'), 10);
    if (!id) return;

    btn.disabled = true;
    btn.classList.add('is-loading');

    var finish = function () {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      var el = document.getElementById('offcanvas-cart');
      if (el && window.bootstrap && window.bootstrap.Offcanvas) {
        window.bootstrap.Offcanvas.getOrCreateInstance(el).show();
      }
      document.dispatchEvent(new CustomEvent('om:cart:updated'));
    };

    var cart = document.querySelector('cart-container');
    if (cart && typeof cart.add === 'function') {
      var res = cart.add({ id: id, quantity: 1 });
      if (res && typeof res.then === 'function') res.then(finish, finish);
      else setTimeout(finish, 500);
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: id, quantity: 1 }),
    }).then(finish, finish);
  });

  /* ------------------------------------------- Trust badge detail panels */

  // One panel open at a time. Rises from the bottom on phones, slides in from
  // the side on wider screens — both are the same element, the difference is
  // purely CSS, so the behaviour here stays identical.
  var Sheet = (function () {
    var openEl = null;
    var lastTrigger = null;

    function focusables(root) {
      return Array.prototype.filter.call(
        root.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
    }

    function close() {
      if (!openEl) return;
      var el = openEl;
      openEl = null;

      el.classList.remove('is-open');
      document.body.classList.remove('om-sheet-open');

      var trigger = lastTrigger;
      lastTrigger = null;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      var done = function () {
        el.hidden = true;
        // Send focus back where it came from, so keyboard users are not dumped
        // at the top of the document.
        if (trigger) trigger.focus();
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        done();
      } else {
        setTimeout(done, 280);
      }
    }

    function open(id, trigger) {
      var el = document.getElementById(id);
      if (!el) return;
      if (openEl) close();

      lastTrigger = trigger || null;
      openEl = el;

      el.hidden = false;
      document.body.classList.add('om-sheet-open');
      // Next frame, so the transition runs from its starting position.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { el.classList.add('is-open'); });
      });

      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      var first = el.querySelector('[data-om-sheet-close]');
      if (first) first.focus({ preventScroll: true });
    }

    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!t || !t.closest) return;

      var opener = t.closest('[data-om-sheet-open]');
      if (opener) {
        event.preventDefault();
        open(opener.getAttribute('data-om-sheet-open'), opener);
        return;
      }

      if (t.closest('[data-om-sheet-close]')) {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!openEl) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      // Keep tabbing inside the open panel.
      if (event.key === 'Tab') {
        var items = focusables(openEl);
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    // Swipe the panel down to dismiss it on touch devices.
    function bindDrag(panel) {
      if (panel.dataset.omDrag) return;
      panel.dataset.omDrag = '1';

      var startY = 0, delta = 0, dragging = false;

      panel.addEventListener('touchstart', function (e) {
        if (window.innerWidth >= 768) return;
        var body = panel.querySelector('.om-sheet__body');
        // Only start a drag when the content is already scrolled to the top,
        // otherwise the gesture belongs to the scrolling text.
        if (body && body.scrollTop > 0) return;
        dragging = true;
        startY = e.touches[0].clientY;
        delta = 0;
        panel.style.transition = 'none';
      }, { passive: true });

      panel.addEventListener('touchmove', function (e) {
        if (!dragging) return;
        delta = e.touches[0].clientY - startY;
        if (delta > 0) panel.style.transform = 'translateY(' + delta + 'px)';
      }, { passive: true });

      panel.addEventListener('touchend', function () {
        if (!dragging) return;
        dragging = false;
        panel.style.transition = '';
        panel.style.transform = '';
        if (delta > 90) close();
      });
    }

    // A transformed or filtered ancestor becomes the containing block for
    // position:fixed, which would dock the panel to that element instead of
    // the viewport. Sections with scroll animations do exactly that. Moving
    // each panel to <body> makes the positioning reliable wherever the block
    // that owns it happens to render.
    function reparent() {
      document.querySelectorAll('[data-om-sheet]').forEach(function (el) {
        if (el.parentElement !== document.body) document.body.appendChild(el);
      });
    }

    function bindAll() {
      reparent();
      document.querySelectorAll('[data-om-sheet] .om-sheet__panel').forEach(bindDrag);
    }

    return { open: open, close: close, bindAll: bindAll };
  })();

  /* ------------------------------------------------------------- D10: init */

  function init() {
    document.querySelectorAll('[data-om-price-block]').forEach(function (block) {
      var root = block.closest('.product-contents') || document;
      if (root.dataset && root.dataset.omBound) return;
      if (root.dataset) root.dataset.omBound = '1';
      new ProductArea(root);
    });

    // Quantity/ATC can exist without the price block.
    var buy = document.querySelector('[data-om-buy]');
    if (buy) {
      var host = buy.closest('.product-contents');
      if (host && !host.dataset.omBound) {
        host.dataset.omBound = '1';
        new ProductArea(host);
      }
    }

    var bar = document.querySelector('[data-om-sticky-atc]');
    if (bar) StickyAtc(bar);

    bindCollapsibles(document);
    bindRecentlyViewed();
    Sheet.bindAll();
    bindStats();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cart drawer contents are replaced on every cart update; re-bind then.
  document.addEventListener('shopify:section:load', function () {
    bindCollapsibles(document);
  });
  document.addEventListener('om:cart:updated', function () {
    bindCollapsibles(document);
  });

  window.OMCro = { formatMoney: formatMoney, money: money, bindCollapsibles: bindCollapsibles, sheet: Sheet };
})();
